import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, isRef as _isRef, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createSlots as _createSlots, createCommentVNode as _createCommentVNode } from "vue"

import { computed, inject, watch, type ComputedRef, type Ref } from 'vue';
import { useField, useSubmitCount } from 'vee-validate';
import {
    limitToTwoDecimals,
    formatAmountToTwoDecimals,
    calculateAmountIncludingTax,
} from 'composables/useOcrProducts';
import { useTax } from 'composables/useTax';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Translator } from 'types/injects/translator.type';

interface Props {
    index: number;
    disabled?: boolean;
    canDelete?: boolean;
    taxRates: TaxRate[];
    accountingCodes: AccountingCode[];
    units?: Unit[];
    showAnalyticCode?: boolean;
    currencySymbol?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OcrTaxLineRow',
  props: {
    index: {},
    disabled: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    taxRates: {},
    accountingCodes: {},
    units: { default: () => [] },
    showAnalyticCode: { type: Boolean, default: false },
    currencySymbol: { default: undefined }
  },
  emits: ["request-delete"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $t = inject('$t') as Translator;
const { taxLabel, isEinvoicingCompliant } = useTax();

const submitCount = useSubmitCount();
const fieldOptions = { validateOnValueUpdate: false };
const fieldPath =
    (name: string): (() => string) =>
    () =>
        `taxLines[${props.index}].${name}`;

const gateError = (message: Ref<string | undefined>): ComputedRef<string> =>
    computed(() => (submitCount.value > 0 ? (message.value ?? '') : ''));

const { value: taxRateId, errorMessage: taxRateIdMessage } = useField<number | null>(
    fieldPath('taxRateId'),
    undefined,
    fieldOptions,
);
const taxRateIdError = gateError(taxRateIdMessage);

const { value: description, errorMessage: descriptionMessage } = useField<string>(
    fieldPath('description'),
    undefined,
    fieldOptions,
);
const descriptionError = gateError(descriptionMessage);

const { value: amountIncludingTax, errorMessage: amountIncludingTaxMessage } = useField<
    number | string | null
>(fieldPath('amountIncludingTax'), undefined, fieldOptions);
const amountIncludingTaxError = gateError(amountIncludingTaxMessage);

const { value: amountExcludingTax, errorMessage: amountExcludingTaxMessage } = useField<
    number | string | null
>(fieldPath('amountExcludingTax'), undefined, fieldOptions);
const amountExcludingTaxError = gateError(amountExcludingTaxMessage);

const { value: unit } = useField<string | number | null>(
    fieldPath('unit'),
    undefined,
    fieldOptions,
);

const { value: accountingCode, errorMessage: accountingCodeMessage } = useField<number | null>(
    fieldPath('accountingCode'),
    undefined,
    fieldOptions,
);
const accountingCodeError = gateError(accountingCodeMessage);

const { value: analyticCode } = useField<string | null>(
    fieldPath('analyticCode'),
    undefined,
    fieldOptions,
);

const compliantTaxRates = computed<TaxRate[]>(() =>
    props.taxRates.filter((tax) => isEinvoicingCompliant(tax)),
);

const onAmountIncludingTaxInput = (value: string | number | null): void => {
    amountIncludingTax.value = limitToTwoDecimals(value);
};

const formatAmountIncludingTaxOnBlur = (): void => {
    amountIncludingTax.value = formatAmountToTwoDecimals(amountIncludingTax.value);
};

const onAmountExcludingTaxInput = (value: string | number | null): void => {
    amountExcludingTax.value = limitToTwoDecimals(value);
};

const formatAmountExcludingTaxOnBlur = (): void => {
    amountExcludingTax.value = formatAmountToTwoDecimals(amountExcludingTax.value);
};

watch([amountExcludingTax, taxRateId], () => {
    const taxItem = props.taxRates.find((item) => item.id === taxRateId.value);
    if (!taxItem) {
        return;
    }
    const includingTax = calculateAmountIncludingTax(
        amountExcludingTax.value,
        Number(taxItem.rate),
    );
    if (includingTax !== null) {
        amountIncludingTax.value = formatAmountToTwoDecimals(includingTax);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassSelect = _resolveComponent("CompassSelect")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassTableBodyCell = _resolveComponent("CompassTableBodyCell")!
  const _component_CompassInput = _resolveComponent("CompassInput")!
  const _component_CompassIconButton = _resolveComponent("CompassIconButton")!
  const _component_CompassTooltip = _resolveComponent("CompassTooltip")!
  const _component_CompassTableRow = _resolveComponent("CompassTableRow")!

  return (_openBlock(), _createBlock(_component_CompassTableRow, null, {
    default: _withCtx(() => [
      _createVNode(_component_CompassTableBodyCell, {
        maxWidth: "110px",
        minWidth: "110px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_CompassFormItem, {
            label: "",
            error: _unref(taxRateIdError)
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CompassSelect, {
                modelValue: _unref(taxRateId),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(taxRateId) ? (taxRateId).value = $event : null)),
                class: "ocr-tax-line-row__input",
                disabled: props.disabled,
                filterable: "",
                placeholder: _unref($t).__('ocr.OcrVerificationForm.select_rate'),
                error: !!_unref(taxRateIdError)
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(compliantTaxRates.value, (rate) => {
                    return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                      key: rate.id,
                      label: _unref(taxLabel)(rate),
                      value: rate.id
                    }, null, 8 /* PROPS */, ["label", "value"]))
                  }), 128 /* KEYED_FRAGMENT */))
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["modelValue", "disabled", "placeholder", "error"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["error"])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_component_CompassTableBodyCell, {
        maxWidth: "200px",
        minWidth: "200px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_CompassFormItem, {
            label: "",
            error: _unref(descriptionError)
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CompassInput, {
                modelValue: _unref(description),
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(description) ? (description).value = $event : null)),
                class: "ocr-tax-line-row__input",
                disabled: props.disabled,
                error: !!_unref(descriptionError)
              }, null, 8 /* PROPS */, ["modelValue", "disabled", "error"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["error"])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_component_CompassTableBodyCell, {
        maxWidth: "140px",
        minWidth: "140px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_CompassFormItem, {
            label: "",
            error: _unref(amountExcludingTaxError)
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CompassInput, {
                modelValue: _unref(amountExcludingTax),
                "onUpdate:modelValue": [
                  _cache[2] || (_cache[2] = ($event: any) => (_isRef(amountExcludingTax) ? (amountExcludingTax).value = $event : null)),
                  onAmountExcludingTaxInput
                ],
                class: "ocr-tax-line-row__input",
                disabled: props.disabled,
                error: !!_unref(amountExcludingTaxError),
                inputmode: "decimal",
                onBlur: formatAmountExcludingTaxOnBlur
              }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                (props.currencySymbol)
                  ? {
                      name: "suffix",
                      fn: _withCtx(() => [
                        _createTextVNode(_toDisplayString(props.currencySymbol), 1 /* TEXT */)
                      ]),
                      key: "0"
                    }
                  : undefined
              ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "disabled", "error"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["error"])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_component_CompassTableBodyCell, {
        maxWidth: "140px",
        minWidth: "140px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_CompassFormItem, {
            label: "",
            error: _unref(amountIncludingTaxError)
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CompassInput, {
                modelValue: _unref(amountIncludingTax),
                "onUpdate:modelValue": [
                  _cache[3] || (_cache[3] = ($event: any) => (_isRef(amountIncludingTax) ? (amountIncludingTax).value = $event : null)),
                  onAmountIncludingTaxInput
                ],
                class: "ocr-tax-line-row__input",
                disabled: props.disabled,
                error: !!_unref(amountIncludingTaxError),
                inputmode: "decimal",
                onBlur: formatAmountIncludingTaxOnBlur
              }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                (props.currencySymbol)
                  ? {
                      name: "suffix",
                      fn: _withCtx(() => [
                        _createTextVNode(_toDisplayString(props.currencySymbol), 1 /* TEXT */)
                      ]),
                      key: "0"
                    }
                  : undefined
              ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "disabled", "error"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["error"])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_component_CompassTableBodyCell, {
        maxWidth: "140px",
        minWidth: "140px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_CompassSelect, {
            modelValue: _unref(unit),
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(unit) ? (unit).value = $event : null)),
            class: "ocr-tax-line-row__input",
            disabled: props.disabled,
            filterable: "",
            placeholder: _unref($t).__('ocr.OcrVerificationProductsTable.select_unit')
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CompassDropdownOption, {
                value: null,
                label: _unref($t).__('common.labels.none')
              }, null, 8 /* PROPS */, ["label"]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.units, (unitItem) => {
                return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                  key: unitItem.id,
                  label: unitItem.label,
                  value: unitItem.id
                }, null, 8 /* PROPS */, ["label", "value"]))
              }), 128 /* KEYED_FRAGMENT */))
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["modelValue", "disabled", "placeholder"])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_component_CompassTableBodyCell, {
        maxWidth: "200px",
        minWidth: "200px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_CompassFormItem, {
            label: "",
            error: _unref(accountingCodeError)
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CompassSelect, {
                modelValue: _unref(accountingCode),
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(accountingCode) ? (accountingCode).value = $event : null)),
                class: "ocr-tax-line-row__input",
                disabled: props.disabled,
                filterable: "",
                placeholder: _unref($t).__('ocr.OcrVerificationForm.select_code'),
                error: !!_unref(accountingCodeError)
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassDropdownOption, {
                    value: null,
                    label: _unref($t).__('common.labels.none')
                  }, null, 8 /* PROPS */, ["label"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.accountingCodes, (item) => {
                    return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                      key: item.id,
                      label: `${item.code} - ${item.label}`,
                      value: item.id
                    }, null, 8 /* PROPS */, ["label", "value"]))
                  }), 128 /* KEYED_FRAGMENT */))
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["modelValue", "disabled", "placeholder", "error"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["error"])
        ]),
        _: 1 /* STABLE */
      }),
      (props.showAnalyticCode)
        ? (_openBlock(), _createBlock(_component_CompassTableBodyCell, {
            key: 0,
            maxWidth: "200px",
            minWidth: "200px"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_CompassInput, {
                modelValue: _unref(analyticCode),
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(analyticCode) ? (analyticCode).value = $event : null)),
                class: "ocr-tax-line-row__input",
                disabled: props.disabled,
                placeholder: _unref($t).__('ocr.OcrVerificationProductsTable.analytic_code')
              }, null, 8 /* PROPS */, ["modelValue", "disabled", "placeholder"])
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      _createVNode(_component_CompassTableBodyCell, { idColumn: "delete" }, {
        default: _withCtx(() => [
          (props.canDelete)
            ? (_openBlock(), _createBlock(_component_CompassTooltip, {
                key: 0,
                content: _unref($t).__('common.actions.delete'),
                arrow: false
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassIconButton, {
                    icon: "bin",
                    disabled: props.disabled,
                    variant: _ctx.CompassEnums.IconButtonVariant.DESTRUCTIVE,
                    size: _ctx.CompassEnums.IconButtonSize.MEDIUM,
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (emit('request-delete', props.index)))
                  }, null, 8 /* PROPS */, ["disabled", "variant", "size"])
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["content"]))
            : _createCommentVNode("v-if", true)
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }))
}
}

})